import { useState } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import reactStringReplace from 'react-string-replace'

import { KEYWORD_TEMPLATE_URL } from 'utils/consts.mjs'

import RankedKeywords from '../../../components/tables/RankedKeywords'
import CSVInput from '../../../components/input/CSVInput'
import { translate } from '../../../utils/translation'
import { fonts, palette, mediaQueries } from '../../../styles/variables'
import { getKeywordsCount } from '../../../utils/functions'
import { filterNewKeywords, kwsFromCSV } from '../../../utils/ptDataFromatting'
import { getMaxValuesForProject } from '../../../utils/freeVersionBlock'
import { RowContent } from '../../../components/modal/Modal'
import KeywordTagInput from '../../../components/input/TagInput/KeywordTagInput'
import NotePanel from '../../../components/panels/NotePanel'
import infoIcon from '../../../styles/assets/info.svg'
import { Heading3A, LinkText, Text } from '../../../components/topography'
import MultiLocationSelector from '../../../components/selector/MultiLocationSelector'
import { Desktop, Mobile } from '../../../utils/media'

const Container = styled.div({})

const Title = styled.div({
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  marginBottom: 8,
})

const Content = styled(RowContent)((props) => ({
  gap: '20px',
  alignItems: 'flex-start',
  height: props.height || '400px',
  [mediaQueries.laptop]: {
    flexWrap: 'wrap',
    height: 'auto',
  },
}))

const Column = styled.div(
  ({ width = '50%', justifyContent = 'flex-start' }) => ({
    display: 'flex',
    flexDirection: 'column',
    width,
    height: '100%',
    justifyContent,

    [mediaQueries.laptop]: {
      width: '100% !important',
      ':nth-child(2)': {
        marginTop: '24px',
      },
    },
  }),
)

const handleAddKeywords = (keywords, addedKeywords) => {
  const filtered = filterNewKeywords(keywords, addedKeywords)
  const newKeywordsToAdd = Array.from(new Set([...addedKeywords, ...filtered]))
  return newKeywordsToAdd
}

const handleRemoveKeywords = (keywords, addedKeywords) => {
  const newKeywordsToAdd = addedKeywords.filter(
    (addedKeyword) => !keywords.some((k) => k === addedKeyword),
  )
  return newKeywordsToAdd
}

const RankedKeywordsResponsive = ({ ...rest }) => {
  const isDesktop = useMediaQuery({ minDeviceWidth: 768 })
  return <RankedKeywords {...rest} height={isDesktop ? '274px' : '150px'} />
}

export default ({
  keywordsInProject,
  locations = [],
  availableKeywords,
  kwsToAdd,
  setKwsToAdd,
  selectedLocations,
  setSelectedLocations,
  areLocationsInvalid = false,
  newAddonProject,
  openEditLocationsModal,
}) => {
  const initialCount = getKeywordsCount(keywordsInProject)
  const keywordsCount =
    initialCount + kwsToAdd.length * selectedLocations.length
  const { MAX_KEYWORDS_PER_PROJECT } = getMaxValuesForProject(
    null,
    newAddonProject,
  )
  const [showCSVImport, setShowCSVImport] = useState(false)

  return (
    <Container>
      <Content height="auto">
        <Column>
          <RankedKeywordsResponsive
            availableKeywords={availableKeywords}
            selectedKeywords={kwsToAdd}
            handleAddKeyword={(kws) =>
              setKwsToAdd(
                handleAddKeywords(
                  kws,
                  kwsToAdd,
                  selectedLocations,
                  keywordsInProject,
                ),
              )
            }
            width="100%"
          />
          <Desktop>
            <Title
              data-testid="confirm-locations-label"
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {translate('confirm_locations')}
              <LinkText
                textDecoration="underline"
                onClick={() => openEditLocationsModal()}
              >
                {translate('edit_option')}
              </LinkText>
            </Title>
            <MultiLocationSelector
              direction="top"
              options={locations}
              value={selectedLocations}
              onChange={setSelectedLocations}
              renderValue={(v) => `${v.loc_id}_${v.lang}`}
              renderLabel={(v) => v.name}
            />
            <Text
              data-testid="confirm-locations-error"
              fontSize="12px"
              lineHeight="12px"
              color={palette.danger}
              margin="10px 0 0 0"
            >
              {areLocationsInvalid
                ? translate('no_locations_selected_error')
                : ' '}
            </Text>
          </Desktop>
        </Column>
        <Column>
          <Title>
            {reactStringReplace(
              translate(
                'kw_box_label_step3',
                translate('import_from_csv_clickable'),
              ),
              translate('import_from_csv_clickable'),
              (match, index) => {
                return (
                  <button
                    type="button"
                    key={index}
                    onClick={() => setShowCSVImport(true)}
                    className="tw-inline-block tw-text-orange tw-cursor-pointer tw-bg-transparent tw-border-none tw-text-sm tw-p-0 tw-m-0"
                    style={{
                      fontFamily: fonts.primaryBook,
                    }}
                  >
                    {match}
                  </button>
                )
              },
            )}
          </Title>
          <KeywordTagInput
            initialCount={initialCount}
            tags={kwsToAdd}
            tagWeight={selectedLocations.length}
            maxTags={MAX_KEYWORDS_PER_PROJECT}
            removeOnBackspace
            onChange={setKwsToAdd}
            placeholder={
              kwsToAdd.length === 0
                ? translate('kw_box_placeholder_desktop')
                : ' '
            }
            limitErrorMessageId="reduce_keywords_or_locations"
            descriptionMessageId="track_more_keywords"
            height="330px"
          />
          {selectedLocations.length > 1 && (
            <NotePanel
              icon={infoIcon}
              style={{ marginTop: '15px' }}
              show={kwsToAdd.length > 0}
            >
              <Text
                fontSize="14px"
                data-testid="keyword-tag-input"
                lineHeight="20px"
                color={palette.darkGray}
                boldColor={palette.darkGray}
                dangerouslySetInnerHTML={{
                  __html: translate(
                    'keywords_and_locations_added',
                    kwsToAdd.length,
                    selectedLocations.length,
                    keywordsCount - initialCount,
                  ),
                }}
              />
            </NotePanel>
          )}
          <Mobile>
            <Title
              style={{
                marginTop: '20px',
              }}
            >
              {translate('confirm_locations')}
            </Title>
            <MultiLocationSelector
              direction="top"
              options={locations}
              value={selectedLocations}
              onChange={setSelectedLocations}
              renderValue={(v) => `${v.loc_id}_${v.lang}`}
              renderLabel={(v) => v.name}
            />
            <Text
              fontSize="12px"
              lineHeight="12px"
              color={palette.danger}
              margin="10px 0 0 0"
            >
              {areLocationsInvalid
                ? translate('no_locations_selected_error')
                : ' '}
            </Text>
          </Mobile>
        </Column>
      </Content>
      {showCSVImport && (
        <div style={{ marginTop: '10px' }}>
          <Heading3A>{translate('upload_file_heading')}</Heading3A>
          <Text lineHeight="20px" margin="12px 0">
            {reactStringReplace(
              translate(
                'upload_file_subheading',
                translate('spreadsheet_template'),
              ),
              translate('spreadsheet_template'),
              (match, i) => {
                return (
                  <LinkText
                    key={`title-${i}`}
                    color={palette.orange}
                    target="_blank"
                    href={KEYWORD_TEMPLATE_URL}
                    fontFamily={fonts.primaryBook}
                    style={{
                      textDecoration: 'underline',
                    }}
                  >
                    {match}
                  </LinkText>
                )
              },
            )}
          </Text>
          <CSVInput
            testId="csv-input-add-keywords-panel"
            onAdd={(parsed) => {
              const csvKeywords = kwsFromCSV(parsed)
              setKwsToAdd(
                handleAddKeywords(
                  csvKeywords,
                  kwsToAdd,
                  selectedLocations,
                  keywordsInProject,
                ),
              )
            }}
            onRemove={(parsed) => {
              setKwsToAdd(handleRemoveKeywords(kwsFromCSV(parsed), kwsToAdd))
            }}
          />
        </div>
      )}
    </Container>
  )
}
